import { Controller } from "stimulus"
import { fetchBreakdown, renderBreakdownTable, syncBreakdownFrame } from "../lib/aia_breakdowns"

export default class extends Controller {
  static targets = [
    "fundRow",
    "categoryRow",
    "targetInput",
    "inventoryBeforeTotal",
    "inventoryDeltaTotal",
    "totalTarget",
    "totalState",
    "categoryBeforeTotal",
    "categoryTargetTotal",
    "categoryDeltaTotal",
    "decisionCount",
    "decisions",
    "decisionsEmpty",
    "assetAllocationFrame",
    "equityCountryFrame",
    "equityCapitalisationFrame",
    "currenciesFrame",
    "shareSearchInput",
    "shareSearchResults",
    "shareCategorySelect",
    "shareCategoryResults",
    "strategyName",
    "saveButton",
    "feedback",
    "chatMessages",
    "chatInput",
    "chatSubmitButton",
    "chatFeedback",
    "chatPanel",
    "chatCollapseButton",
    "chatReopenButton",
    "chatResizeHandle"
  ]

  connect() {
    this.chatRequestTimer = null
    this.breakdownsByKind = {}
    this.chatHistory = []
    this.conversationId = this.newConversationId()
    this.strategicDecisions = this.initialStrategicDecisions()
    this.inventoryChanged = false
    this.restoreChatPanelPreferences()
    this.boundScheduleChatPanelHeight = this.scheduleChatPanelHeight.bind(this)
    window.addEventListener("resize", this.boundScheduleChatPanelHeight)
    window.addEventListener("scroll", this.boundScheduleChatPanelHeight, { passive: true })
    this.scheduleChatPanelHeight()
    this.syncAll()
    this.analyticalBreakdownsReady = this.loadAnalyticalBreakdowns()
  }

  // Identifie la conversation pour le journal serveur : un fichier par session de chat, remis à
  // zéro au rechargement de la page, exactement comme l'historique que le navigateur transporte.
  newConversationId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, "").slice(0, 12)

    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  }

  disconnect() {
    window.removeEventListener("resize", this.boundScheduleChatPanelHeight)
    window.removeEventListener("scroll", this.boundScheduleChatPanelHeight)
    if (this.chatPanelHeightFrame) cancelAnimationFrame(this.chatPanelHeightFrame)
    document.body.classList.remove("is-resizing-chat")
  }

  scheduleChatPanelHeight(event) {
    if (event && event.type === "scroll" && !this.desktopChatPanel()) return
    if (this.chatPanelHeightFrame) return

    this.chatPanelHeightFrame = requestAnimationFrame(() => {
      this.chatPanelHeightFrame = null
      this.syncChatPanelHeight()
    })
  }

  syncChatPanelHeight() {
    const stickyTop = this.desktopChatPanel() ? 78 : 12
    const panelTop = Math.max(this.chatPanelTarget.getBoundingClientRect().top, stickyTop)
    const availableHeight = Math.max(320, window.innerHeight - panelTop - 20)

    this.chatPanelTarget.style.setProperty("--aia-chat-height", `${availableHeight}px`)
  }

  collapseChatPanel(event) {
    event.preventDefault()
    this.persistChatPanelWidth()
    this.setChatPanelCollapsed(true)
  }

  expandChatPanel(event) {
    event.preventDefault()
    this.setChatPanelCollapsed(false)
    this.restoreChatPanelWidth()
    this.scheduleChatPanelHeight()
    this.chatInputTarget.focus()
  }

  setChatPanelCollapsed(collapsed, persist = true) {
    this.chatPanelTarget.classList.toggle("is-collapsed", collapsed)
    this.chatCollapseButtonTarget.setAttribute("aria-expanded", (!collapsed).toString())
    this.chatReopenButtonTarget.setAttribute("aria-expanded", (!collapsed).toString())
    if (persist) this.writeChatPreference("collapsed", collapsed ? "1" : "0")
  }

  startChatResize(event) {
    if (!this.desktopChatPanel() || this.chatPanelTarget.classList.contains("is-collapsed")) return
    if (event.pointerType === "mouse" && event.button !== 0) return

    event.preventDefault()
    this.chatResizeStartX = event.clientX
    this.chatResizeStartWidth = this.chatPanelTarget.getBoundingClientRect().width
    this.chatResizePointerId = event.pointerId
    event.currentTarget.setPointerCapture(event.pointerId)
    event.currentTarget.classList.add("is-resizing")
    document.body.classList.add("is-resizing-chat")
  }

  resizeChatPanel(event) {
    if (this.chatResizePointerId !== event.pointerId) return

    const width = this.chatResizeStartWidth + event.clientX - this.chatResizeStartX
    this.applyChatPanelWidth(width)
  }

  stopChatResize(event) {
    if (this.chatResizePointerId !== event.pointerId) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    event.currentTarget.classList.remove("is-resizing")
    document.body.classList.remove("is-resizing-chat")
    this.chatResizePointerId = null
    this.persistChatPanelWidth()
  }

  resizeChatPanelWithKeyboard(event) {
    if (!this.desktopChatPanel()) return

    const limits = this.chatPanelWidthLimits()
    const currentWidth = this.chatPanelTarget.getBoundingClientRect().width
    let width = null

    if (event.key === "ArrowLeft") width = currentWidth - 20
    if (event.key === "ArrowRight") width = currentWidth + 20
    if (event.key === "Home") width = limits.min
    if (event.key === "End") width = limits.max
    if (width === null) return

    event.preventDefault()
    this.applyChatPanelWidth(width)
    this.persistChatPanelWidth()
  }

  applyChatPanelWidth(width) {
    const limits = this.chatPanelWidthLimits()
    const boundedWidth = Math.round(Math.min(limits.max, Math.max(limits.min, width)))

    this.chatPanelTarget.style.width = `${boundedWidth}px`
    this.chatResizeHandleTarget.setAttribute("aria-valuemax", Math.round(limits.max))
    this.chatResizeHandleTarget.setAttribute("aria-valuenow", boundedWidth)
  }

  chatPanelWidthLimits() {
    return {
      min: 280,
      max: Math.max(280, Math.min(680, window.innerWidth * 0.5))
    }
  }

  desktopChatPanel() {
    return window.innerWidth > 1100
  }

  restoreChatPanelPreferences() {
    this.setChatPanelCollapsed(this.readChatPreference("collapsed") === "1", false)
    this.restoreChatPanelWidth()
  }

  restoreChatPanelWidth() {
    if (!this.desktopChatPanel()) return

    const width = parseInt(this.readChatPreference("width"), 10)
    this.applyChatPanelWidth(Number.isFinite(width) ? width : 320)
  }

  persistChatPanelWidth() {
    if (!this.desktopChatPanel() || this.chatPanelTarget.classList.contains("is-collapsed")) return

    this.writeChatPreference(
      "width",
      Math.round(this.chatPanelTarget.getBoundingClientRect().width).toString()
    )
  }

  readChatPreference(name) {
    try {
      return window.localStorage.getItem(`arbitrage-ia:eos-ia:${name}`)
    } catch (error) {
      return null
    }
  }

  writeChatPreference(name, value) {
    try {
      window.localStorage.setItem(`arbitrage-ia:eos-ia:${name}`, value)
    } catch (error) {
      // Le panneau reste utilisable lorsque le stockage local est indisponible.
    }
  }

  growChatInput() {
    const input = this.chatInputTarget
    input.style.height = "auto"
    // box-sizing: border-box (Bootstrap) : scrollHeight ignore les bordures, que la hauteur posée
    // inclut. On mesure l'écart au lieu de le coder en dur, sinon la barre de défilement
    // apparaît une ligne trop tôt.
    const borders = input.offsetHeight - input.clientHeight
    const maximum = this.maxChatInputHeight()
    const height = Math.min(input.scrollHeight + borders, maximum)
    input.style.height = `${height}px`
    // Pas de gouttière de défilement tant que le champ peut encore grandir.
    input.style.overflowY = height < maximum ? "hidden" : "auto"
  }

  maxChatInputHeight() {
    return 132
  }

  async sendChatMessage(event) {
    // Dans le textarea, Maj+Entrée reste un retour à la ligne : seule Entrée seule envoie.
    if (event.type === "keydown" && event.shiftKey) return

    event.preventDefault()
    event.stopPropagation()
    if (this.chatBusy) return

    const message = this.chatInputTarget.value.trim()
    if (!message) {
      this.chatFeedbackTarget.textContent = "Écrivez un message avant de l'envoyer."
      this.chatInputTarget.focus()
      return
    }

    this.chatBusy = true
    this.chatFeedbackTarget.textContent = ""
    this.chatInputTarget.disabled = true
    this.chatSubmitButtonTarget.disabled = true
    this.appendChatMessage("user", message)
    const pendingMessage = this.appendChatMessage("assistant", "EOS IA réfléchit...", true)
    const abortController = new AbortController()

    await this.analyticalBreakdownsReady
    this.chatRequestTimer = setTimeout(() => abortController.abort(), 35000)

    fetch(this.data.get("chat-url"), {
      method: "POST",
      headers: this.jsonHeaders(),
      signal: abortController.signal,
      body: JSON.stringify({
        message: message,
        conversation_id: this.conversationId,
        inventory: this.assistantInventoryPayload(),
        analytical_breakdowns: this.assistantAnalyticalBreakdownsPayload(),
        history: this.chatHistory
      })
    }).then((response) => {
      return response.json().then((data) => {
        if (!response.ok) throw new Error(data.error || "Impossible de contacter EOS IA.")
        return data
      })
    }).then((data) => {
      this.chatHistory.push({ role: "user", content: message })
      this.chatHistory.push({ role: "assistant", payload: JSON.stringify(data.assistant) })
      this.chatHistory = this.chatHistory.slice(-8)
      pendingMessage.replaceWith(this.assistantResponseMessage(data.assistant))
    }).catch((error) => {
      pendingMessage.remove()
      this.chatFeedbackTarget.textContent = error.name === "AbortError"
        ? "EOS IA met trop de temps à répondre. Réessayez dans quelques instants."
        : error.message
    }).finally(() => {
      clearTimeout(this.chatRequestTimer)
      this.chatRequestTimer = null
      this.chatBusy = false
      this.chatInputTarget.disabled = false
      this.chatSubmitButtonTarget.disabled = false
      this.chatInputTarget.value = ""
      this.growChatInput()
      this.chatInputTarget.focus()
      this.scrollChatToBottom()
    })
  }

  appendChatMessage(role, text, pending = false) {
    const message = document.createElement("p")
    message.className = `aia-chat__message is-${role}${pending ? " is-pending" : ""}`
    message.textContent = text
    this.chatMessagesTarget.appendChild(message)
    this.scrollChatToBottom()
    return message
  }

  assistantResponseMessage(response) {
    const message = document.createElement("div")
    message.className = "aia-chat__message is-assistant"

    if (!response) {
      this.appendAssistantResponseBadge(message, "error")
      this.appendChatParagraph(message, "EOS IA n'a renvoyé aucune réponse.")
      return message
    }

    this.appendAssistantResponseBadge(message, response.response_type)

    const movements = response.movements || []
    const impacts = response.impacts || []
    const scope = response.scope || []

    if (response.understanding) {
      const calloutType = response.response_type === "information" ? "information" : "understanding"
      this.appendChatParagraph(
        message,
        response.understanding,
        `aia-chat__callout aia-chat__callout--${calloutType}`
      )
    }
    if (scope.length > 0) this.appendAssistantScope(message, scope)
    if (movements.length > 0) this.appendMovementsTable(message, movements)
    if (impacts.length > 0) this.appendImpactsTable(message, impacts)
    this.appendAssistantAlerts(message, response.alerts || [])
    if (response.clarification) {
      this.appendChatParagraph(
        message,
        response.clarification.question,
        "aia-chat__callout aia-chat__callout--question"
      )
    }
    this.appendAssistantConfirmation(message, response)

    return message
  }

  appendAssistantResponseBadge(message, responseType) {
    const labels = {
      clarification: "À préciser",
      proposal: "Proposition",
      information: "Information",
      error: "Erreur"
    }
    const label = labels[responseType]
    if (!label) return

    const badge = document.createElement("span")
    badge.className = `aia-chat__response-badge is-${responseType}`
    badge.textContent = label
    message.appendChild(badge)
  }

  appendAssistantConfirmation(message, response) {
    const confirmation = response.confirmation
    if (!confirmation || !confirmation.required) return

    const movements = (response.movements || []).filter((movement) => this.isApplicableMovement(movement))
    if (movements.length === 0) {
      if (confirmation.prompt) this.appendChatParagraph(message, confirmation.prompt)
      return
    }

    const button = document.createElement("button")
    button.type = "button"
    button.className = "aia-btn aia-btn--primary aia-chat__apply"
    button.textContent = `Appliquer ${movements.length === 1 ? "le mouvement" : "les mouvements"} à l'Inventaire`
    button.addEventListener("click", () => this.applyAssistantMovements(movements, button, response))
    message.appendChild(button)
  }

  isApplicableMovement(movement) {
    return movement &&
      movement.share_id !== null &&
      movement.share_id !== undefined &&
      movement.target_weight !== null &&
      movement.target_weight !== undefined
  }

  async applyAssistantMovements(movements, button, response = null) {
    const label = button.textContent
    const missing = []
    let applied = 0

    if (this.outdatedMovements(movements)) {
      button.disabled = true
      button.textContent = "Proposition périmée"
      this.chatFeedbackTarget.textContent = "L'Inventaire a changé depuis cette proposition. Redemandez-la à EOS IA pour qu'elle soit recalculée sur l'Inventaire courant."
      return
    }

    button.disabled = true
    button.textContent = "Application en cours..."

    for (const movement of movements) {
      const row = this.inventoryRowFor(movement.share_id) || await this.insertProposedShare(movement)
      if (!row) {
        missing.push(movement.label)
        continue
      }

      row.querySelector(".aia-target").value = this.normalizedWeight(movement.target_weight)
      applied += 1
    }

    if (applied === 0) {
      button.disabled = false
      button.textContent = label
      this.chatFeedbackTarget.textContent = "Aucun mouvement n'a pu être appliqué à l'Inventaire."
      return
    }

    this.inventoryChanged = true
    if (missing.length === 0) this.captureStrategicDecision(response)
    this.syncAll()
    this.showInventoryTab()
    button.textContent = `Appliqué à l'Inventaire · ${applied} ${applied === 1 ? "ligne" : "lignes"}`
    this.chatFeedbackTarget.textContent = missing.length > 0
      ? `Lignes non appliquées : ${missing.join(", ")}.`
      : ""
  }

  initialStrategicDecisions() {
    try {
      const value = JSON.parse(this.data.get("strategic-decisions") || "[]")
      return Array.isArray(value) ? value : []
    } catch (_error) {
      return []
    }
  }

  // Une proposition chiffre des cibles absolues à partir de l'Inventaire du moment. Appliquée après
  // coup, elle écraserait les mouvements passés entre-temps, et la suite des décisions ne décrirait
  // plus le chemin réel jusqu'à l'Inventaire cible.
  outdatedMovements(movements) {
    return movements.some((movement) => {
      const row = this.inventoryRowFor(movement.share_id)
      const current = row ? this.number(row.querySelector(".aia-target").value) : 0

      return Math.abs(current - this.number(movement.current_weight)) > 0.011
    })
  }

  // Les décisions s'empilent dans l'ordre où elles ont été appliquées : elles décrivent le chemin
  // parcouru par l'Inventaire, que le Réplicateur rejoue ensuite décision après décision. Écraser
  // une décision antérieure porterait le dernier palier en oubliant le premier.
  captureStrategicDecision(response) {
    const decision = response && response.strategic_decision
    if (!decision) return

    this.strategicDecisions.push(decision)
  }

  inventoryRowFor(shareId) {
    return this.fundRowTargets.find((item) => item.dataset.shareId === String(shareId))
  }

  // Un fonds proposé par EOS IA peut être absent de l'Inventaire : Rails l'a choisi dans une
  // catégorie EOS, on insère sa ligne par le même appel que la fenêtre « Ajouter un fonds ».
  async insertProposedShare(movement) {
    if (!movement.eos_category_id) return null

    try {
      const url = `${this.data.get("share-payload-url")}` +
        `&share_id=${encodeURIComponent(movement.share_id)}` +
        `&eos_category_id=${encodeURIComponent(movement.eos_category_id)}`
      const response = await fetch(url, { method: "GET", headers: { "Accept": "application/json" } })
      if (!response.ok) return null

      const data = await response.json()
      this.insertInventoryRow(data)
      this.ensureCategoryRow(data)
      return this.inventoryRowFor(movement.share_id)
    } catch (error) {
      return null
    }
  }

  showInventoryTab() {
    const tab = this.element.querySelector('[data-toggle="tab"][href="#c_inventory"]')
    if (tab) tab.click()
  }

  appendChatParagraph(message, text, className = null) {
    const paragraph = document.createElement("p")
    if (className) paragraph.className = className
    paragraph.textContent = text
    message.appendChild(paragraph)
  }

  appendAssistantScope(message, scope) {
    const list = document.createElement("ul")
    list.className = "aia-chat-list"

    scope.forEach((item) => {
      const entry = document.createElement("li")
      entry.textContent = item.reason ? `${item.label} : ${item.reason}` : item.label
      list.appendChild(entry)
    })

    message.appendChild(list)
  }

  appendMovementsTable(message, movements) {
    const total = movements.reduce((sum, movement) => sum + this.number(movement.delta_weight), 0)

    this.appendChatTable(message, {
      caption: "Mouvements proposés",
      headings: ["Ligne", "Avant", "Cible", "Écart"],
      rows: movements.map((movement) => [
        movement.label,
        this.assistantWeight(movement.current_weight),
        this.assistantWeight(movement.target_weight),
        this.deltaCell(movement.delta_weight)
      ]),
      footer: ["Total", "", "", { text: this.totalDeltaText(total), className: this.totalDeltaClass(total) }]
    })
  }

  // Chaque dimension a sa propre assiette : empiler les catégories EOS, la géographie actions et la
  // capitalisation dans un seul tableau donnait un total de 300 %. Un tableau par dimension, dans
  // l'ordre où Rails les a chiffrées.
  appendImpactsTable(message, impacts) {
    const dimensions = [...new Set(impacts.map((impact) => impact.dimension))]

    dimensions.forEach((dimension) => {
      this.appendDimensionImpactsTable(
        message,
        dimension,
        impacts.filter((impact) => impact.dimension === dimension)
      )
    })
  }

  appendDimensionImpactsTable(message, dimension, impacts) {
    const rows = impacts.filter((impact) => this.hasAnalyticalWeight(impact))
    if (rows.length === 0) return

    const heading = this.assistantDimensionLabel(dimension)
    const total = this.formatTotalPercent(
      rows.reduce((sum, impact) => sum + this.number(impact.target_weight), 0)
    )
    const moved = rows.some((impact) => Math.abs(this.number(impact.delta_weight)) > 0.005)

    if (!moved) {
      this.appendChatTable(message, {
        caption: `Position actuelle · ${heading}`,
        headings: [heading, "Pondération"],
        rows: rows.map((impact) => [impact.label, this.assistantWeight(impact.target_weight)]),
        footer: ["Total", total]
      })
      return
    }

    this.appendChatTable(message, {
      caption: `Effet sur la ventilation · ${heading}`,
      headings: [heading, "Avant", "Après", "Écart"],
      rows: rows.map((impact) => [
        impact.label,
        this.assistantWeight(impact.before_weight),
        this.assistantWeight(impact.target_weight),
        this.deltaCell(impact.delta_weight)
      ]),
      footer: ["Total", "", total, ""]
    })
  }

  // L'écart est le seul chiffre coloré : ▲ vert à la hausse, ▼ rouge à la baisse, gris quand la
  // ligne ne bouge pas, exactement comme dans l'Inventaire. La classe se lit sur la valeur
  // numérique, jamais sur le texte déjà formaté.
  deltaCell(value) {
    return { text: this.assistantDelta(value), className: this.deltaClass(this.number(value)) }
  }

  // Une cellule est un texte, ou un texte accompagné de sa classe pour la colonne « Écart ».
  chatTableCell(value, index) {
    const cell = document.createElement("td")
    const content = value !== null && typeof value === "object" ? value : { text: value, className: null }

    cell.textContent = content.text
    if (index > 0) cell.className = content.className ? `aia-num ${content.className}` : "aia-num"

    return cell
  }

  hasAnalyticalWeight(impact) {
    return Math.abs(this.number(impact.before_weight)) > 0.005 ||
      Math.abs(this.number(impact.target_weight)) > 0.005
  }

  appendChatTable(message, { caption, headings, rows, footer }) {
    const table = document.createElement("table")
    const captionElement = document.createElement("caption")
    const head = document.createElement("thead")
    const headRow = document.createElement("tr")
    const body = document.createElement("tbody")

    table.className = "aia-chat-table"
    captionElement.textContent = caption
    table.appendChild(captionElement)

    headings.forEach((label, index) => {
      const heading = document.createElement("th")
      heading.scope = "col"
      heading.textContent = label
      if (index > 0) heading.className = "aia-num"
      headRow.appendChild(heading)
    })

    rows.forEach((cells) => {
      const row = document.createElement("tr")
      cells.forEach((value, index) => row.appendChild(this.chatTableCell(value, index)))
      body.appendChild(row)
    })

    head.appendChild(headRow)
    table.appendChild(head)
    table.appendChild(body)

    if (footer) {
      const foot = document.createElement("tfoot")
      const footRow = document.createElement("tr")
      footer.forEach((value, index) => footRow.appendChild(this.chatTableCell(value, index)))
      foot.appendChild(footRow)
      table.appendChild(foot)
      message.appendChild(table)
      return
    }

    message.appendChild(table)
  }

  assistantDimensionLabel(dimension) {
    return {
      eos_category: "Catégorie EOS",
      asset: "Classe d'actifs",
      equity_country: "Zone géographique",
      equity_capitalisation: "Capitalisation",
      currency: "Devise"
    }[dimension] || "Exposition"
  }

  appendAssistantAlerts(message, alerts) {
    alerts.forEach((alert) => {
      const note = document.createElement("p")
      note.className = "aia-chat__note"
      note.textContent = alert.level === "info" ? alert.message : `Alerte : ${alert.message}`
      message.appendChild(note)
    })
  }

  scrollChatToBottom() {
    this.chatMessagesTarget.scrollTop = this.chatMessagesTarget.scrollHeight
  }

  assistantWeight(value) {
    return value === null || value === undefined ? "non déterminé" : this.formatPercent(this.number(value))
  }

  assistantDelta(value) {
    if (value === null || value === undefined) return "non déterminé"
    if (Math.abs(this.number(value)) < 0.005) return "stable"
    return this.formatSigned(this.number(value))
  }

  onTargetChange(event) {
    this.invalidateStrategicDecisions()
    this.inventoryChanged = true
    this.syncRow(event.currentTarget.closest(".aia-eos-row"))
    this.syncAll()
  }

  onTargetKeydown(event) {
    const step = event.key === "ArrowUp" ? 0.01 : event.key === "ArrowDown" ? -0.01 : 0
    if (step === 0) return

    event.preventDefault()
    this.applyTargetValue(event.currentTarget, this.number(event.currentTarget.value) + step)
  }

  completeTarget(event) {
    const input = event.currentTarget.closest(".aia-eos-row").querySelector(".aia-target")
    const others = this.targetInputTargets
      .filter((item) => item !== input)
      .reduce((sum, item) => sum + this.number(item.value), 0)

    this.applyTargetValue(input, 100 - others)
  }

  applyTargetValue(input, value) {
    this.invalidateStrategicDecisions()
    input.value = this.normalizedWeight(value)
    this.inventoryChanged = true
    this.syncRow(input.closest(".aia-eos-row"))
    this.syncAll()
  }

  normalizedWeight(value) {
    return Math.min(Math.max(Math.round(this.number(value) * 100) / 100, 0), 100)
  }

  searchShareByIsin(event) {
    event.preventDefault()
    clearTimeout(this.shareSearchTimer)
    this.shareSearchTimer = setTimeout(() => this.performShareSearch(), 250)
  }

  submitShareSearch(event) {
    event.preventDefault()
    clearTimeout(this.shareSearchTimer)
    this.performShareSearch()
  }

  performShareSearch() {
    const query = this.shareSearchInputTarget.value.trim()

    if (query.length < 3) {
      this.shareSearchResultsTarget.innerHTML = "<p class=\"mt-3 text-muted text-center\"><i>- Coller un code ISIN -</i></p>"
      return
    }

    fetch(`${this.data.get("share-search-url")}&isin=${encodeURIComponent(query)}`, {
      method: "GET",
      headers: { "Accept": "application/json" }
    }).then((response) => response.json()).then((data) => {
      this.shareSearchResultsTarget.innerHTML = data.html
    })
  }

  loadSharesByCategory(event) {
    event.preventDefault()
    const categoryId = this.shareCategorySelectTarget.value

    if (!categoryId) {
      this.shareCategoryResultsTarget.innerHTML = "<p class=\"mt-3 text-muted text-center\"><i>- Sélectionner une catégorie EOS -</i></p>"
      return
    }

    this.shareCategoryResultsTarget.innerHTML = "<p class=\"mt-3 text-muted text-center\"><i>Chargement...</i></p>"

    fetch(`${this.data.get("category-shares-url")}&eos_category_id=${encodeURIComponent(categoryId)}`, {
      method: "GET",
      headers: { "Accept": "application/json" }
    }).then((response) => response.json()).then((data) => {
      this.shareCategoryResultsTarget.innerHTML = data.html
    })
  }

  addShareFromSearch(event) {
    event.preventDefault()
    const button = event.currentTarget
    const shareId = button.dataset.shareId

    if (this.shareAlreadyInInventory(shareId)) {
      this.showShareSearchMessage(button, "Cette part est déjà présente dans l'inventaire.", "danger")
      return
    }

    button.disabled = true

    fetch(`${this.data.get("share-payload-url")}&share_id=${encodeURIComponent(shareId)}&eos_category_id=${encodeURIComponent(button.dataset.eosCategoryId)}`, {
      method: "GET",
      headers: { "Accept": "application/json" }
    }).then((response) => {
      return response.json().catch(() => ({})).then((data) => {
        if (!response.ok) throw new Error(data.error || "Impossible d'ajouter cette part à l'inventaire.")
        return data
      })
    }).then((data) => {
      this.insertInventoryRow(data)
      this.ensureCategoryRow(data)
      this.inventoryChanged = true
      this.syncAll()
      this.showShareSearchMessage(button, `Cette part a bien été ajoutée dans l'inventaire : ${this.escape(data.share_name)} - ${this.escape(data.isin)}`, "success")
    }).catch((error) => {
      button.disabled = false
      this.showShareSearchMessage(button, this.escape(error.message), "danger")
    })
  }

  save() {
    const total = this.totalTargetWeight()
    if (Math.abs(total - 100) > 0.05) {
      this.feedbackTarget.textContent = "Le total cible doit être égal à 100 %."
      this.feedbackTarget.className = "aia-save-feedback is-error"
      return
    }

    this.saveButtonTarget.disabled = true
    this.feedbackTarget.textContent = "Enregistrement en cours..."
    this.feedbackTarget.className = "aia-save-feedback"

    fetch(this.data.get("save-url"), {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        strategy: {
          name: this.strategyNameTarget.value,
          inventory: this.inventoryPayload(),
          strategic_decisions: this.strategicDecisions
        }
      })
    }).then((response) => {
      if (!response.ok) return response.json().then((data) => Promise.reject(data))
      return response.json()
    }).then((data) => {
      window.location.href = data.redirect_url
    }).catch((error) => {
      this.saveButtonTarget.disabled = false
      this.feedbackTarget.textContent = error.error || "Impossible d'enregistrer la stratégie."
      this.feedbackTarget.className = "aia-save-feedback is-error"
    })
  }

  syncAll() {
    this.fundRowTargets.forEach((row) => this.syncRow(row))
    this.syncCategories()
    this.syncAnalyticalBreakdowns()
    this.syncTotal()
    this.syncDecisions()
  }

  syncRow(row) {
    if (!row) return

    const input = row.querySelector(".aia-target")
    const before = this.number(input.dataset.before)
    const target = this.number(input.value)
    const delta = target - before
    const deltaElement = row.querySelector("[data-delta]")
    const bar = row.querySelector("[data-bar]")
    const accent = row.querySelector("[data-accent]")

    deltaElement.textContent = Math.abs(delta) < 0.01 ? "–" : this.formatSigned(delta)
    deltaElement.className = `aia-num ${this.deltaClass(delta)}`
    input.classList.toggle("is-up", delta > 0.01)
    input.classList.toggle("is-down", delta < -0.01)
    row.classList.toggle("is-up", delta > 0.01)
    row.classList.toggle("is-down", delta < -0.01)
    accent.className = this.deltaClass(delta)
    bar.style.width = `${Math.min(Math.max(target * 1.4, 0), 100)}%`
  }

  syncCategories() {
    const totals = {}
    let beforeTotal = 0
    let targetTotal = 0

    this.fundRowTargets.forEach((row) => {
      const key = this.categoryKey(row.dataset.categoryId, row.dataset.categoryName)
      const input = row.querySelector(".aia-target")
      const before = this.number(input.dataset.before)
      const target = this.number(input.value)

      totals[key] = totals[key] || {
        id: row.dataset.categoryId,
        name: row.dataset.categoryName,
        before: 0,
        target: 0
      }
      totals[key].before += before
      totals[key].target += target
      beforeTotal += before
      targetTotal += target
    })

    this.categoryRowTargets.forEach((row) => {
      const key = this.categoryKey(row.dataset.categoryId, row.dataset.categoryName)
      const total = totals[key] || { before: 0, target: 0 }
      const delta = total.target - total.before
      const targetElement = row.querySelector("[data-category-target]")
      const deltaElement = row.querySelector("[data-category-delta]")
      const bar = row.querySelector("[data-category-bar]")
      const accent = row.querySelector("[data-accent]")

      targetElement.textContent = this.formatPercent(total.target)
      deltaElement.textContent = Math.abs(delta) < 0.01 ? "–" : this.formatSigned(delta)
      deltaElement.className = `aia-num ${this.deltaClass(delta)}`
      row.classList.toggle("is-up", delta > 0.01)
      row.classList.toggle("is-down", delta < -0.01)
      accent.className = this.deltaClass(delta)
      bar.style.width = `${Math.min(Math.max(total.target * 1.4, 0), 100)}%`
    })

    this.syncCategoryTotals(beforeTotal, targetTotal)
  }

  syncCategoryTotals(beforeTotal, targetTotal) {
    const delta = targetTotal - beforeTotal

    if (this.hasCategoryBeforeTotalTarget) this.categoryBeforeTotalTarget.textContent = this.formatTotalPercent(beforeTotal)
    if (this.hasCategoryTargetTotalTarget) this.categoryTargetTotalTarget.textContent = this.formatTotalPercent(targetTotal)
    if (this.hasCategoryDeltaTotalTarget) {
      this.categoryDeltaTotalTarget.textContent = this.totalDeltaText(delta)
      this.categoryDeltaTotalTarget.className = `aia-num ${this.totalDeltaClass(delta)}`
    }
  }

  syncTotal() {
    const total = this.totalTargetWeight()
    const beforeTotal = this.beforeTotalWeight()
    const delta = total - beforeTotal

    if (this.hasInventoryBeforeTotalTarget) this.inventoryBeforeTotalTarget.textContent = this.formatTotalPercent(beforeTotal)
    this.totalTargetTarget.textContent = this.formatTotalPercent(total)
    if (this.hasInventoryDeltaTotalTarget) {
      this.inventoryDeltaTotalTarget.textContent = this.totalDeltaText(delta)
      this.inventoryDeltaTotalTarget.className = `aia-num ${this.totalDeltaClass(delta)}`
    }
    this.totalStateTarget.textContent = this.totalIsBalanced(total) ? "Équilibré" : "À ajuster"
    this.totalStateTarget.classList.toggle("is-error", !this.totalIsBalanced(total))
  }

  syncDecisions() {
    const movements = this.modifiedRows()
    if (this.strategicDecisions.length > 0) {
      this.decisionCountTarget.textContent = this.strategicDecisions.length
      this.decisionsEmptyTarget.classList.add("d-none")
      this.saveButtonTarget.textContent = `Valider · ${this.strategicDecisions.length} ${this.strategicDecisions.length === 1 ? "décision" : "décisions"}`
      this.decisionsTarget.innerHTML = this.strategicDecisions.map((decision) => {
        const movements = decision.reference_movements || []
        const movementCount = movements.length
        const storedWeight = decision.reference && decision.reference.reallocated_weight
        const reallocated = storedWeight == null
          ? movements.reduce((sum, movement) => sum + Math.abs(Math.min(this.number(movement.delta_weight), 0)), 0)
          : this.number(storedWeight)

        return `
          <div class="aia-decision-card aia-decision-card--strategic is-down">
            <strong>${this.escape(decision.description || "Réallocation analytique")}</strong>
            <small>${movementCount} ${movementCount === 1 ? "mouvement" : "mouvements"}</small>
            <b>${this.formatPercent(reallocated)} réalloués</b>
          </div>
        `
      }).join("")
      return
    }

    this.decisionCountTarget.textContent = 0
    this.decisionsEmptyTarget.classList.remove("d-none")
    this.saveButtonTarget.textContent = movements.length > 0 ? `Valider · ${movements.length} mouvements` : "Valider les mouvements"
    this.decisionsTarget.innerHTML = ""
  }

  invalidateStrategicDecisions() {
    if (this.strategicDecisions.length === 0) return

    this.strategicDecisions = []
  }

  insertInventoryRow(data) {
    const totalRow = this.element.querySelector(".aia-eos-total--inventory")
    if (!totalRow) return

    totalRow.insertAdjacentHTML("beforebegin", this.inventoryRowTemplate(data))
  }

  inventoryRowTemplate(data) {
    return `
      <div class="aia-eos-row"
           data-arbitrage-ia-compositeur-target="fundRow"
           data-share-id="${this.escape(data.share_id)}"
           data-share-name="${this.escape(data.share_name)}"
           data-isin="${this.escape(data.isin)}"
           data-fund-name="${this.escape(data.fund_name)}"
           data-category-id="${this.escape(data.eos_category_id)}"
           data-category-name="${this.escape(data.eos_category_name)}"
           data-asset-breakdown="${this.escape(JSON.stringify(data.asset_breakdown || {}))}"
           data-analytical-breakdowns="${this.escape(JSON.stringify(data.analytical_breakdowns || {}))}">
        <i data-accent></i>
        <span class="aia-eos-row__main"><strong>${this.escape(data.share_name)}</strong><small>${this.escape(data.isin)}</small></span>
        <span>${this.escape(data.eos_category_name)}</span>
        <span class="aia-num">${this.formatPercent(this.number(data.before_weight))}</span>
        <span class="aia-target-cell">
          <input class="aia-target"
                 type="number"
                 min="0"
                 max="100"
                 step="0.01"
                 value="${this.number(data.target_weight)}"
                 data-before="${this.number(data.before_weight)}"
                 data-arbitrage-ia-compositeur-target="targetInput"
                 data-action="input->arbitrage-ia-compositeur#onTargetChange keydown->arbitrage-ia-compositeur#onTargetKeydown">
          <small>%</small>
          <button class="aia-target-complete"
                  type="button"
                  title="Compléter cette ligne pour atteindre 100 % de total cible"
                  data-action="click->arbitrage-ia-compositeur#completeTarget">
            = 100
          </button>
        </span>
        <span class="aia-num" data-delta>–</span>
        <span class="aia-visual-bar"><b data-bar style="width:0%"></b></span>
      </div>
    `
  }

  ensureCategoryRow(data) {
    const categoryId = `${data.eos_category_id || ""}`
    const existingRow = this.categoryRowTargets.find((row) => row.dataset.categoryId === categoryId)
    if (existingRow) return

    const totalRow = this.element.querySelector(".aia-eos-total--categories")
    if (!totalRow) return

    totalRow.insertAdjacentHTML("beforebegin", `
      <div class="aia-eos-row"
           data-arbitrage-ia-compositeur-target="categoryRow"
           data-category-id="${this.escape(data.eos_category_id)}"
           data-category-name="${this.escape(data.eos_category_name)}">
        <i data-accent></i>
        <span>${this.escape(data.eos_category_name)}</span>
        <span class="aia-num" data-category-before>–</span>
        <span class="aia-num" data-category-target>–</span>
        <span class="aia-num" data-category-delta>–</span>
        <span class="aia-visual-bar"><b data-category-bar style="width:0%"></b></span>
      </div>
    `)
  }

  shareAlreadyInInventory(shareId) {
    return this.fundRowTargets.some((row) => row.dataset.shareId === `${shareId}`)
  }

  showShareSearchMessage(button, message, level) {
    const container = button.closest("[id^='aia-share-search-result']")
    if (!container) return

    container.querySelectorAll(".alert").forEach((element) => element.remove())
    container.insertAdjacentHTML("afterbegin", `<div class="alert alert-${level} text-center">${message}</div>`)
  }

  loadAnalyticalBreakdowns() {
    return fetchBreakdown(this.data.get("breakdowns-url")).then((data) => {
      this.breakdownConfigs().forEach((config) => {
        if (!config.frame) return

        this.breakdownsByKind[config.kind] = renderBreakdownTable(
          config.frame,
          config,
          data[config.kind] || []
        )
        this.syncBreakdown(config)
      })
    }).catch(() => {
      this.breakdownConfigs().forEach((config) => {
        if (config.frame) {
          config.frame.innerHTML = `<div class="aia-empty-state">${config.errorMessage}</div>`
        }
      })
    })
  }

  syncAnalyticalBreakdowns() {
    this.breakdownConfigs().forEach((config) => this.syncBreakdown(config))
  }

  syncBreakdown(config) {
    if (!config.frame || !this.breakdownsByKind[config.kind]) return

    syncBreakdownFrame(config.frame, config, this.fundEntries())
  }

  fundEntries() {
    return this.fundRowTargets.map((row) => {
      const input = row.querySelector(".aia-target")
      return { targetWeight: this.number(input.value), breakdowns: this.rowBreakdowns(row) }
    })
  }

  rowBreakdowns(row) {
    try {
      return JSON.parse(row.dataset.analyticalBreakdowns || "{}")
    } catch (error) {
      return {}
    }
  }

  breakdownConfigs() {
    return [
      {
        kind: "asset",
        dataKey: "asset",
        frame: this.hasAssetAllocationFrameTarget ? this.assetAllocationFrameTarget : null,
        title: "Classe d'actifs",
        emptyMessage: "Aucune allocation d'actifs disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition par allocation d'actifs."
      },
      {
        kind: "equity_country",
        dataKey: "equity_country",
        frame: this.hasEquityCountryFrameTarget ? this.equityCountryFrameTarget : null,
        title: "Zone géographique",
        assetMultiplier: "equities",
        consolidateCountries: true,
        emptyMessage: "Aucune répartition géographique actions disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition géographique actions."
      },
      {
        kind: "equity_capitalisation",
        dataKey: "equity_capitalisation",
        frame: this.hasEquityCapitalisationFrameTarget ? this.equityCapitalisationFrameTarget : null,
        title: "Taille de capitalisation",
        emptyMessage: "Aucune répartition par tailles de capitalisation disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition par tailles de capitalisation."
      },
      {
        kind: "currencies",
        dataKey: "currencies",
        frame: this.hasCurrenciesFrameTarget ? this.currenciesFrameTarget : null,
        title: "Devise",
        emptyMessage: "Aucune répartition par devises disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition par devises."
      }
    ]
  }

  inventoryPayload() {
    return this.fundRowTargets.map((row) => {
      const input = row.querySelector(".aia-target")
      const before = this.number(input.dataset.before)
      const target = this.number(input.value)

      return {
        share_id: row.dataset.shareId,
        share_name: row.dataset.shareName,
        isin: row.dataset.isin,
        fund_name: row.dataset.fundName,
        eos_category_id: row.dataset.categoryId,
        eos_category_name: row.dataset.categoryName,
        before_weight: before,
        target_weight: target,
        delta_weight: target - before,
        source: "manual"
      }
    })
  }

  assistantInventoryPayload() {
    return this.fundRowTargets.map((row) => {
      const input = row.querySelector(".aia-target")

      return {
        share_id: row.dataset.shareId,
        eos_category_id: row.dataset.categoryId,
        target_weight: this.number(input.value)
      }
    })
  }

  assistantAnalyticalBreakdownsPayload() {
    return this.breakdownConfigs().reduce((payload, config) => {
      if (!config.frame) return payload

      const dimension = config.kind === "currencies" ? "currency" : config.kind
      payload[dimension] = Array.from(
        config.frame.querySelectorAll("[data-breakdown-row]")
      ).map((row) => ({
        key: row.dataset.breakdownSlug,
        before_weight: this.number(row.dataset.breakdownBefore),
        target_weight: this.number(row.dataset.breakdownTargetValue)
      }))
      return payload
    }, {})
  }

  modifiedRows() {
    return this.fundRowTargets.filter((row) => {
      const input = row.querySelector(".aia-target")
      return Math.abs(this.number(input.value) - this.number(input.dataset.before)) >= 0.01
    })
  }

  totalTargetWeight() {
    return this.targetInputTargets.reduce((sum, input) => sum + this.number(input.value), 0)
  }

  beforeTotalWeight() {
    return this.targetInputTargets.reduce((sum, input) => sum + this.number(input.dataset.before), 0)
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
    }
  }

  categoryKey(id, name) {
    return `${id || "none"}:${name || ""}`
  }

  number(value) {
    const number = parseFloat((value || "0").toString().replace(",", "."))
    return isNaN(number) ? 0 : number
  }

  formatPercent(value) {
    return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
  }

  formatTotalPercent(value) {
    return this.formatPercent(this.displayTotalValue(this.number(value)))
  }

  displayTotalValue(value) {
    return this.totalIsBalanced(value) ? 100 : value
  }

  totalIsBalanced(value) {
    return Math.abs(this.number(value) - 100) <= this.totalTolerance()
  }

  totalTolerance() {
    return 0.05
  }

  totalDeltaText(value) {
    return Math.abs(this.number(value)) <= this.totalTolerance() ? "–" : this.formatSigned(value)
  }

  totalDeltaClass(value) {
    return Math.abs(this.number(value)) <= this.totalTolerance() ? "is-neutral" : this.deltaClass(value)
  }

  formatSigned(value) {
    const sign = value > 0 ? "+" : "-"
    return `${sign}${Math.abs(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
  }

  deltaClass(value) {
    if (value > 0.01) return "is-up"
    if (value < -0.01) return "is-down"
    return "is-neutral"
  }

  escape(value) {
    const element = document.createElement("div")
    element.textContent = value || ""
    return element.innerHTML
  }
}
